import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components';
import { apiJson, ApiError } from '@/lib/api';

export type FormSource = {
  id: number;
  jurisdiction_code: string;
  index_url: string;
  source_kind: string;
  county: string | null;
  crawl_enabled: boolean;
  last_crawled_at: string | null;
  last_crawl_status: string | null;
  version_count: number;
  pending_count: number;
};

export type FormSourcesResponse = {
  sources: FormSource[];
  enabled_count: number;
  pending_version_count: number;
  latest_job: CrawlJob | null;
};

export type LoadSeedResult = {
  jurisdictions_created: number;
  jurisdictions_updated: number;
  sources_created: number;
  sources_updated: number;
};

type CrawlCounts = {
  new: number;
  unchanged: number;
  superseded: number;
  errors: number;
};

type CrawlEvent = {
  at: string;
  level: 'info' | 'success' | 'warn' | 'error' | string;
  message: string;
};

type CrawlSourceProgress = {
  id: number;
  jurisdiction_code: string;
  index_url: string;
  source_kind: string | null;
  status: string;
  percent: number;
  links_found: number;
  links_processed: number;
  current_url: string | null;
  current_file: string | null;
  counts: CrawlCounts;
  status_text: string | null;
  started_at: string | null;
  finished_at: string | null;
};

export type CrawlJob = {
  job_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | string;
  trigger: string;
  source_id: number | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  phase: string;
  headline: string;
  percent: number;
  current_source_id: number | null;
  current_source_code: string | null;
  current_url: string | null;
  current_file: string | null;
  sources_total: number;
  sources_done: number;
  sources_failed: number;
  pdfs_found: number;
  pdfs_processed: number;
  counts: CrawlCounts;
  sources: CrawlSourceProgress[];
  events: CrawlEvent[];
  error: string | null;
};

type CrawlStartResult = {
  queued: boolean;
  job_id: string;
  source_id: number | null;
  source_count: number;
  message: string;
  job: CrawlJob | null;
};

const JOB_STORAGE_KEY = 'harbor.divorceCrawlJobId';
const PHASES = ['queued', 'fetching_index', 'discovering', 'downloading', 'done'] as const;
const PHASE_LABEL: Record<string, string> = {
  queued: 'Queued',
  fetching_index: 'Index',
  discovering: 'Discover',
  downloading: 'Download',
  done: 'Done',
  failed: 'Failed',
  running: 'Running',
};

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

function parseDetail(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: unknown };
      if (typeof j.detail === 'string') return j.detail;
      if (j.detail) return JSON.stringify(j.detail);
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

function parseTime(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

export function formatWhen(iso: string | null): string {
  const ms = parseTime(iso);
  if (ms == null) return iso ? iso : 'Never';
  return new Date(ms).toLocaleString();
}

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatRelative(iso: string): string {
  const ms = parseTime(iso);
  if (ms == null) return '';
  const delta = Date.now() - ms;
  if (delta < 2000) return 'just now';
  if (delta < 60_000) return `${Math.floor(delta / 1000)}s ago`;
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  return formatWhen(iso);
}

function stateName(code: string): string {
  return STATE_NAMES[code] ?? code;
}

function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '');
  }
}

function crawlFreshness(iso: string | null): 'fresh' | 'aging' | 'stale' | 'never' {
  const ms = parseTime(iso);
  if (ms == null) return 'never';
  const days = (Date.now() - ms) / 86_400_000;
  if (days <= 8) return 'fresh';
  if (days <= 16) return 'aging';
  return 'stale';
}

function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}

function ProgressRing({
  percent,
  running,
}: {
  percent: number;
  running: boolean;
}) {
  const r = 40;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, percent));
  const offset = c - (clamped / 100) * c;
  return (
    <div className='relative h-28 w-28 shrink-0'>
      <svg viewBox='0 0 100 100' className='h-28 w-28'>
        <circle cx='50' cy='50' r={r} fill='none' stroke='#e5e7eb' strokeWidth='8' />
        <circle
          cx='50'
          cy='50'
          r={r}
          fill='none'
          stroke='currentColor'
          strokeWidth='8'
          strokeLinecap='round'
          strokeDasharray={c}
          strokeDashoffset={offset}
          className={`text-nova-primary transition-[stroke-dashoffset] duration-500 ${
            running ? 'drop-shadow-sm' : ''
          }`}
          transform='rotate(-90 50 50)'
        />
      </svg>
      <div className='absolute inset-0 flex flex-col items-center justify-center'>
        <span className='text-2xl font-semibold tabular-nums text-nova-foreground'>
          {Math.round(clamped)}
          <span className='text-sm font-medium text-nova-muted-foreground'>%</span>
        </span>
      </div>
    </div>
  );
}

function PhaseStepper({ phase, status }: { phase: string; status: string }) {
  const current =
    status === 'failed' ? 'failed' : status === 'completed' ? 'done' : phase;
  const currentIdx = PHASES.indexOf(current as (typeof PHASES)[number]);
  return (
    <ol className='flex flex-wrap items-center gap-1 text-xs'>
      {PHASES.map((p, i) => {
        const done = current === 'done' || (currentIdx >= 0 && i < currentIdx);
        const active = p === current || (current === 'running' && p === 'queued');
        return (
          <li key={p} className='flex items-center gap-1'>
            {i > 0 ? <span className='text-nova-muted-foreground'>→</span> : null}
            <span
              className={`rounded-md px-2 py-0.5 ${
                done
                  ? 'bg-green-50 text-green-800'
                  : active
                    ? 'bg-nova-primary/10 font-medium text-nova-primary'
                    : 'bg-nova-muted text-nova-muted-foreground'
              }`}
            >
              {PHASE_LABEL[p]}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function Stat({
  label,
  value,
  tone = 'gray',
}: {
  label: string;
  value: number | string;
  tone?: 'gray' | 'green' | 'amber' | 'red' | 'blue';
}) {
  const tones = {
    gray: 'bg-nova-muted text-nova-foreground',
    green: 'bg-green-50 text-green-800',
    amber: 'bg-amber-50 text-amber-900',
    red: 'bg-red-50 text-red-800',
    blue: 'bg-blue-50 text-blue-800',
  };
  return (
    <div className={`min-w-[5.5rem] flex-1 rounded-[var(--nova-radius)] px-3 py-2 ${tones[tone]}`}>
      <div className='text-lg font-semibold tabular-nums'>{value}</div>
      <div className='text-[11px] uppercase tracking-wide opacity-70'>{label}</div>
    </div>
  );
}

function sourceBarClass(status: string): string {
  if (status === 'completed') return 'bg-green-500';
  if (status === 'failed' || status === 'needs_manual') return 'bg-amber-500';
  if (status === 'downloading' || status === 'fetching_index' || status === 'discovering') {
    return 'bg-nova-primary';
  }
  return 'bg-gray-300';
}

function EventLevelDot({ level }: { level: string }) {
  const cls =
    level === 'success'
      ? 'bg-green-500'
      : level === 'warn'
        ? 'bg-amber-400'
        : level === 'error'
          ? 'bg-red-500'
          : 'bg-gray-300';
  return <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${cls}`} />;
}

type Props = {
  onError: (message: string | null) => void;
};

export function DivorceCrawlerPanel({ onError }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(JOB_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [seedResult, setSeedResult] = useState<LoadSeedResult | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const rememberJob = (id: string | null) => {
    setJobId(id);
    try {
      if (id) sessionStorage.setItem(JOB_STORAGE_KEY, id);
      else sessionStorage.removeItem(JOB_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  };

  const sourcesQuery = useQuery({
    queryKey: ['admin', 'divorce-sources'],
    queryFn: () =>
      apiJson<FormSourcesResponse>(getToken, '/v1/admin/divorce/sources'),
  });

  useEffect(() => {
    const latest = sourcesQuery.data?.latest_job;
    if (!latest) return;
    if (latest.status === 'queued' || latest.status === 'running') {
      rememberJob(latest.job_id);
    } else if (!jobId) {
      rememberJob(latest.job_id);
    }
    // Hydrate once from the sources payload; jobQuery takes over after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcesQuery.data?.latest_job?.job_id, sourcesQuery.data?.latest_job?.status]);

  const jobQuery = useQuery({
    queryKey: ['admin', 'divorce-crawl-job', jobId],
    enabled: jobId != null,
    queryFn: () =>
      apiJson<CrawlJob>(
        getToken,
        `/v1/admin/divorce/sources/crawl/${encodeURIComponent(jobId!)}`,
      ),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'queued' || status === 'running' ? 1500 : false;
    },
  });

  const job = jobQuery.data ?? sourcesQuery.data?.latest_job ?? null;
  const live = job?.status === 'queued' || job?.status === 'running';
  const now = useNow(Boolean(live));

  useEffect(() => {
    if (!job) return;
    if (job.status === 'completed' || job.status === 'failed') {
      void qc.invalidateQueries({ queryKey: ['admin', 'divorce-sources'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'divorce-forms'] });
    }
  }, [job?.status, job?.job_id, qc]);

  const sources = sourcesQuery.data?.sources ?? [];
  const enabledCount = sourcesQuery.data?.enabled_count ?? 0;
  const pendingVersions = sourcesQuery.data?.pending_version_count ?? 0;
  const neverCrawled = sources.length > 0 && sources.every((s) => !s.last_crawled_at);

  const elapsedMs = useMemo(() => {
    if (!job) return 0;
    const start = parseTime(job.started_at) ?? parseTime(job.created_at);
    const end = parseTime(job.finished_at) ?? now;
    if (start == null) return 0;
    return Math.max(0, end - start);
  }, [job, now]);

  const etaLabel = useMemo(() => {
    if (!job || !live || job.percent < 8 || elapsedMs < 4000) return null;
    const remaining = elapsedMs * ((100 - job.percent) / job.percent);
    return `~${formatDuration(remaining)} left`;
  }, [job, live, elapsedMs]);

  const rateLabel = useMemo(() => {
    if (!job || elapsedMs < 8000 || job.pdfs_processed < 2) return null;
    const perMin = job.pdfs_processed / (elapsedMs / 60_000);
    return `${perMin.toFixed(1)} PDFs/min`;
  }, [job, elapsedMs]);

  const loadSeed = useMutation({
    mutationFn: () =>
      apiJson<LoadSeedResult>(getToken, '/v1/admin/divorce/sources/load-seed', {
        method: 'POST',
      }),
    onSuccess: (result) => {
      onError(null);
      setSeedResult(result);
      setNotice(
        result.sources_created > 0
          ? 'Wave 1 sources are in. Crawl whenever you are ready.'
          : 'Seed already loaded — sources refreshed from JSON.',
      );
      void qc.invalidateQueries({ queryKey: ['admin', 'divorce-sources'] });
    },
    onError: (err) => onError(parseDetail(err)),
  });

  const crawl = useMutation({
    mutationFn: (sourceId?: number) =>
      apiJson<CrawlStartResult>(getToken, '/v1/admin/divorce/sources/crawl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sourceId != null ? { source_id: sourceId } : {}),
      }),
    onSuccess: (result) => {
      onError(null);
      setNotice(result.message);
      if (result.job_id) rememberJob(result.job_id);
      if (result.job) {
        qc.setQueryData(['admin', 'divorce-crawl-job', result.job_id], result.job);
      }
      void qc.invalidateQueries({ queryKey: ['admin', 'divorce-sources'] });
    },
    onError: (err) => onError(parseDetail(err)),
  });

  const busy = loadSeed.isPending || crawl.isPending || live;
  const events = [...(job?.events ?? [])].reverse();

  return (
    <div className='space-y-5'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h2 className='text-sm font-semibold text-nova-foreground'>Form sources & crawler</h2>
          <p className='mt-1 text-sm text-nova-muted-foreground'>
            Load the Wave 1 judiciary indexes, then watch the worker fetch every PDF.
            Beat also crawls Mondays at 09:00 UTC.
          </p>
        </div>
        <div className='flex flex-wrap gap-2'>
          <Button
            variant='secondary'
            size='sm'
            loading={loadSeed.isPending}
            disabled={busy && !live}
            onClick={() => void loadSeed.mutate()}
          >
            Load Wave 1 sources
          </Button>
          <Button
            size='sm'
            loading={crawl.isPending}
            disabled={busy || enabledCount === 0}
            onClick={() => void crawl.mutate(undefined)}
          >
            {live ? 'Crawling…' : 'Crawl all now'}
          </Button>
        </div>
      </div>

      {sources.length === 0 && !sourcesQuery.isLoading ? (
        <p className='rounded-[var(--nova-radius)] bg-amber-50 px-3 py-2 text-sm text-amber-800'>
          Nothing to crawl yet. Load Wave 1 sources to create the official index
          pages, then start a crawl.
        </p>
      ) : null}
      {neverCrawled && !live ? (
        <p className='rounded-[var(--nova-radius)] bg-amber-50 px-3 py-2 text-sm text-amber-800'>
          Sources are loaded but have never been crawled. Start a crawl and this
          panel will show percent complete, the current file, and a live log.
        </p>
      ) : null}
      {notice ? <p className='text-sm text-green-700'>{notice}</p> : null}

      {seedResult ? (
        <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
          <Stat
            label='Jurisdictions new'
            value={seedResult.jurisdictions_created}
            tone='green'
          />
          <Stat label='Jurisdictions updated' value={seedResult.jurisdictions_updated} />
          <Stat label='Sources new' value={seedResult.sources_created} tone='blue' />
          <Stat label='Sources updated' value={seedResult.sources_updated} />
        </div>
      ) : null}

      {job ? (
        <div
          className={`rounded-[var(--nova-radius)] border p-4 ${
            job.status === 'failed'
              ? 'border-red-200 bg-red-50/40'
              : job.status === 'completed'
                ? 'border-green-200 bg-green-50/30'
                : 'border-nova-primary/20 bg-nova-primary/[0.03]'
          }`}
        >
          <div className='flex flex-col gap-4 sm:flex-row sm:items-center'>
            <ProgressRing percent={job.percent} running={live} />
            <div className='min-w-0 flex-1 space-y-2'>
              <div className='flex flex-wrap items-center gap-2'>
                <span
                  className={`rounded-md px-2 py-0.5 text-xs font-medium ${
                    job.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : job.status === 'failed'
                        ? 'bg-red-100 text-red-800'
                        : job.status === 'queued'
                          ? 'bg-nova-muted text-nova-foreground'
                          : 'bg-nova-primary/10 text-nova-primary'
                  }`}
                >
                  {job.status}
                  {job.trigger === 'beat' ? ' · scheduled' : ''}
                </span>
                <span className='text-xs text-nova-muted-foreground'>
                  {job.sources_done}/{job.sources_total} sources
                </span>
              </div>
              <p className='text-base font-medium text-nova-foreground'>{job.headline}</p>
              <div className='flex flex-wrap gap-x-3 gap-y-1 text-xs text-nova-muted-foreground'>
                <span>{formatDuration(elapsedMs)} elapsed</span>
                {etaLabel ? <span>{etaLabel}</span> : null}
                {rateLabel ? <span>{rateLabel}</span> : null}
                {job.current_file ? (
                  <span className='truncate font-mono text-nova-muted-foreground'>
                    {job.current_file}
                  </span>
                ) : null}
              </div>
              <div className='h-2 overflow-hidden rounded-full bg-nova-muted'>
                <div
                  className={`h-full rounded-full transition-[width] duration-500 ${
                    job.status === 'failed' ? 'bg-red-400' : 'bg-nova-primary'
                  } ${live ? 'animate-pulse' : ''}`}
                  style={{ width: `${Math.max(2, job.percent)}%` }}
                />
              </div>
              <PhaseStepper phase={job.phase} status={job.status} />
            </div>
          </div>

          <div className='mt-4 flex flex-wrap gap-2'>
            <Stat label='New' value={job.counts.new} tone='green' />
            <Stat label='Unchanged' value={job.counts.unchanged} />
            <Stat label='Revised' value={job.counts.superseded} tone='amber' />
            <Stat
              label='Errors'
              value={job.counts.errors}
              tone={job.counts.errors ? 'red' : 'gray'}
            />
            <Stat
              label='PDFs'
              value={`${job.pdfs_processed}${job.pdfs_found ? `/${job.pdfs_found}` : ''}`}
              tone='blue'
            />
          </div>

          {job.sources.length > 0 ? (
            <div className='mt-4 space-y-2'>
              {job.sources.map((s) => (
                <div
                  key={s.id}
                  className={`rounded-[var(--nova-radius)] border bg-nova-card/80 px-3 py-2 ${
                    s.id === job.current_source_id && live
                      ? 'border-nova-primary/40 ring-1 ring-nova-ring/20'
                      : 'border-nova-border'
                  }`}
                >
                  <div className='flex items-center justify-between gap-3'>
                    <div className='min-w-0'>
                      <div className='flex flex-wrap items-center gap-2'>
                        <span className='text-sm font-semibold text-nova-foreground'>
                          {s.jurisdiction_code}
                        </span>
                        <span className='truncate text-xs text-nova-muted-foreground'>
                          {stateName(s.jurisdiction_code)}
                        </span>
                        <span className='rounded bg-nova-muted px-1.5 py-0.5 text-[10px] uppercase text-nova-muted-foreground'>
                          {s.status.split('_').join(' ')}
                        </span>
                      </div>
                      <p className='mt-0.5 truncate text-xs text-nova-muted-foreground'>
                        {s.current_file
                          ? `${s.links_processed}/${s.links_found || '—'} · ${s.current_file}`
                          : s.status_text || hostOf(s.index_url)}
                      </p>
                    </div>
                    <div className='text-right text-xs tabular-nums text-nova-muted-foreground'>
                      <div>{s.percent}%</div>
                      <div className='text-[10px] text-nova-muted-foreground'>
                        {s.counts.new} new
                      </div>
                    </div>
                  </div>
                  <div className='mt-2 h-1.5 overflow-hidden rounded-full bg-nova-muted'>
                    <div
                      className={`h-full rounded-full transition-[width] duration-300 ${sourceBarClass(
                        s.status,
                      )}`}
                      style={{ width: `${Math.max(s.status === 'queued' ? 0 : 3, s.percent)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {events.length > 0 ? (
            <div className='mt-4'>
              <h3 className='mb-2 text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
                Activity
              </h3>
              <ol className='max-h-48 space-y-1.5 overflow-auto rounded-[var(--nova-radius)] bg-nova-card/70 p-3'>
                {events.map((ev, i) => (
                  <li key={`${ev.at}-${i}`} className='flex gap-2 text-xs'>
                    <EventLevelDot level={ev.level} />
                    <span className='w-16 shrink-0 tabular-nums text-nova-muted-foreground'>
                      {formatRelative(ev.at)}
                    </span>
                    <span className='text-nova-foreground'>{ev.message}</span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
        </div>
      ) : null}

      {sources.length > 0 ? (
        <div>
          <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
            <h3 className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
              Coverage
            </h3>
            <p className='text-xs text-nova-muted-foreground'>
              {enabledCount} enabled · {pendingVersions} pending review ·{' '}
              {sources.filter((s) => crawlFreshness(s.last_crawled_at) === 'fresh').length}{' '}
              fresh
            </p>
          </div>
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b border-nova-border text-left text-xs uppercase text-nova-muted-foreground'>
                  <th className='px-2 py-2'>State</th>
                  <th className='px-2 py-2'>Index</th>
                  <th className='px-2 py-2'>Last crawl</th>
                  <th className='px-2 py-2'>Status</th>
                  <th className='px-2 py-2'>Versions</th>
                  <th className='px-2 py-2' />
                </tr>
              </thead>
              <tbody>
                {sources.map((s) => {
                  const freshness = crawlFreshness(s.last_crawled_at);
                  const liveRow = live && job?.current_source_id === s.id;
                  return (
                    <tr
                      key={s.id}
                      className={`border-b border-nova-border ${
                        liveRow ? 'bg-nova-primary/5' : ''
                      }`}
                    >
                      <td className='px-2 py-2'>
                        <div className='flex items-center gap-2'>
                          <span
                            className={`h-2 w-2 rounded-full ${
                              freshness === 'fresh'
                                ? 'bg-green-500'
                                : freshness === 'aging'
                                  ? 'bg-amber-400'
                                  : freshness === 'stale'
                                    ? 'bg-red-400'
                                    : 'bg-gray-300'
                            }`}
                            title={freshness}
                          />
                          <span className='font-medium'>{s.jurisdiction_code}</span>
                          {!s.crawl_enabled ? (
                            <span className='text-xs text-nova-muted-foreground'>off</span>
                          ) : null}
                        </div>
                        <div className='text-[11px] text-nova-muted-foreground'>
                          {stateName(s.jurisdiction_code)}
                        </div>
                      </td>
                      <td className='max-w-xs truncate px-2 py-2 text-xs text-nova-muted-foreground'>
                        <a
                          href={s.index_url}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='hover:underline'
                          title={s.index_url}
                        >
                          {hostOf(s.index_url)}
                        </a>
                        <div className='text-[11px] text-nova-muted-foreground'>{s.source_kind}</div>
                      </td>
                      <td className='whitespace-nowrap px-2 py-2 text-xs text-nova-muted-foreground'>
                        {formatWhen(s.last_crawled_at)}
                      </td>
                      <td className='px-2 py-2 text-xs text-nova-muted-foreground'>
                        {liveRow
                          ? job?.headline
                          : (s.last_crawl_status ?? '—')}
                      </td>
                      <td className='whitespace-nowrap px-2 py-2 text-xs text-nova-muted-foreground'>
                        {s.pending_count} pending / {s.version_count}
                      </td>
                      <td className='px-2 py-2 text-right'>
                        <Button
                          variant='ghost'
                          size='sm'
                          disabled={busy || !s.crawl_enabled}
                          onClick={() => void crawl.mutate(s.id)}
                        >
                          Crawl
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
